/* eslint-disable no-redeclare */
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { addBreadcrumb, captureException } from '@sentry/react'
import type { OperationDefinitionNode } from 'graphql'
import { print } from 'graphql/language/printer'
import { capitalize, isArray, omitBy, pickBy } from 'lodash-es'
import { isPresent } from 'ts-extras'
import type {
	CountryCode,
	GQLResponse,
	GQLResponseErr,
	GQLResponseOk,
	LanguageCode,
	MutationNode,
	NonNull,
	Nullable,
	Obj
} from '~/types'
import { STOREFRONT_CONFIG } from './constants'
import { getCurrentCountry } from './country'
import { AukError, GQLError } from './errors'
import { isNonEmptyArray, parseNumber } from './helpers'
import { extractConnections } from './shopify'

export function gidToId<T extends string, R extends number>(
	gid: T
): T extends `gid://shopify/${string}/${R}` ? R : number {
	const id = gid?.split('/')?.pop()
	if (id) {
		const num = parseNumber(id) as any
		return isNaN(num) ? gid : num
	}
	return gid as any
}

export function toGraphId<T extends number | string>(id: T, type: 'product' | 'variant'): GID {
	if (id?.toString().includes('gid://')) return id as GID
	switch (type) {
		case 'product':
			return `gid://shopify/Product/${id as number}`
		case 'variant':
			return `gid://shopify/ProductVariant/${id as number}`
	}
}

export function isOk<T>(res: GQLResponse<T> | Error | undefined): res is NonNull<GQLResponseOk<T>> {
	if (res instanceof Error) return false
	return isPresent(res) && isPresent(res.data)
}

export function isError<T>(res: GQLResponse<T> | undefined): res is GQLResponseErr {
	const errors = res?.errors
	if (isArray(errors)) return errors.length > 0
	return isPresent(errors)
}

interface InContextVars {
	country: CountryCode
	language: LanguageCode
}

/** Country is not required to be provided as it is added by {@link shopifyFetch} */
type RequiredVars<T> = Omit<T, keyof InContextVars> & Partial<InContextVars>
type AllVars<T extends Record<string, any>> = T & InContextVars

export async function shopifyFetch<T, V>(
	operation: TypedDocumentNode<T, V>,
	variables: RequiredVars<V>,
	signal?: AbortSignal
): Promise<GQLResponse<T>> {
	// Allow country to be overridden if set
	const vars: InContextVars = {
		country: getCurrentCountry().iso_code,
		language: window.localization.language.shop_locale.locale.toUpperCase() as LanguageCode
	}
	return getShopifyData(operation, { ...vars, ...variables } as AllVars<V>, signal)
}

function getDocumentName(doc: TypedDocumentNode): string | null {
	const node = doc.definitions.find((def): def is OperationDefinitionNode => def.kind === 'OperationDefinition')
	return node ? node.name?.value + capitalize(node.operation) : null
}

async function getShopifyData<T, V extends Record<string, any>>(
	gqlQuery: TypedDocumentNode<T, V>,
	variables: V,
	signal?: AbortSignal
): Promise<GQLResponse<T>> {
	const query = typeof gqlQuery === 'string' ? gqlQuery : print(gqlQuery)
	try {
		const response = await fetch(`${STOREFRONT_CONFIG.domain}/api/2025-10/graphql.json`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'application/json',
				'X-Shopify-Storefront-Access-Token': STOREFRONT_CONFIG.token
			},
			body: JSON.stringify({
				query,
				variables
			}),
			signal
		})
		if (response.ok) {
			const data: GQLResponse<T> = await response.json()
			return extractData(data)
		} else {
			const status = `[${response.status}: ${response.statusText}] `
			const message = (await response.text()).replace(/\s+/g, ' ')
			addBreadcrumb({
				type: 'storefront-api',
				message: status + message,
				level: 'warning',
				data: { 'http.status': response.status }
			})
			return { data: null, errors: [new AukError(message)] }
		}
	} catch (e) {
		const err = e as Error
		addBreadcrumb({
			type: 'storefront-api',
			level: 'error',
			message: err.message,
			data: { operation: getDocumentName(gqlQuery), ...variables, error: err.name, stack: err.stack }
		})
		return {
			data: null,
			errors: [new AukError(`${getDocumentName(gqlQuery) ?? gqlQuery.kind} operation failed`, e, { variables })]
		}
	}
}

export function extractData<T>(res: GQLResponse<T>): GQLResponse<T> {
	// TODO: Return data even if errors are present
	if (isNonEmptyArray(res.errors)) {
		return { data: res.data, errors: extractErrors(res) } as GQLResponseErr
	} else if (isArray(res.data)) {
		return { data: res.data }
	} else if (res.data) {
		let data: Record<string, any> = res.data as any
		/** Extract data if response has {@link MutationNode} shape */
		if ((data as MutationNode).__typename === 'Mutation') {
			data = omitBy(data, (_, key) => key === '__typename')
			data = Object.values(data)[0]
		}
		const errors = extractErrors({ ...res, data: data as any })
		// Extract response data. Can't use data key as mutations return an abritrary name
		const resultDict = omitBy(data, (_, key) => key.toLowerCase().includes('errors'))
		const entries = Object.values(resultDict) // Should only be entry left in response now
		if (entries.length > 1) {
			const err = new AukError('Unexpected query result shape')
			console.error(err.message, entries)
			captureException(err)
			return { data: null, errors: errors !== null ? [err, ...errors] : [err] }
		} else {
			return {
				data: extractConnections(entries.pop()),
				errors
			} as GQLResponseOk<T>
		}
	} else {
		return res
	}
}

/**
 * Extract [`userErrors`]({@link UserErrors}) which are nested
 * on a different level than top level [`errors`]({@link GQLError})
 **/
export function extractErrors<T>(res: Partial<GQLResponse<T>>): GQLError[] | null {
	const extract = (data: Nullable<Obj | Obj[]>) =>
		Object.values(pickBy(data, (_, key) => key.toLowerCase().includes('errors')))
	const result = [extract(res), extract(res.data as Obj | Obj[])]
		.flat(3)
		.filter(isPresent)
		.map(err => new GQLError(err))
	return result.length > 0 ? result : null
}
